import { createTheme } from '@mui/material/styles';
import { tokens } from './tokens';

export const getTheme = (mode = 'dark') => {
  const c = tokens.colors[mode] || tokens.colors.dark;

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.accentPrimary,
        contrastText: mode === 'dark' ? '#0a0c10' : '#ffffff',
      },
      secondary: {
        main: c.accentSecondary,
        contrastText: mode === 'dark' ? '#0a0c10' : '#ffffff',
      },
      background: {
        default: c.bgPrimary,
        paper: c.bgCard,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
      },
      divider: c.borderSubtle,
    },
    typography: {
      fontFamily: tokens.fonts.sans,
      h1: {
        fontFamily: tokens.fonts.serif,
        fontWeight: 600,
        letterSpacing: '-0.025em',
        lineHeight: 1.15,
        color: c.textPrimary,
      },
      h2: {
        fontFamily: tokens.fonts.serif,
        fontWeight: 600,
        letterSpacing: '-0.02em',
        lineHeight: 1.18,
        color: c.textPrimary,
      },
      h3: {
        fontFamily: tokens.fonts.serif,
        fontWeight: 600,
        letterSpacing: '-0.015em',
        lineHeight: 1.25,
        color: c.textPrimary,
      },
      h4: {
        fontFamily: tokens.fonts.serif,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.3,
        color: c.textPrimary,
      },
      h5: {
        fontFamily: tokens.fonts.sans,
        fontWeight: 600,
        letterSpacing: '-0.01em',
        lineHeight: 1.38,
        color: c.textPrimary,
      },
      h6: {
        fontFamily: tokens.fonts.sans,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        lineHeight: 1.4,
        color: c.textPrimary,
      },
      subtitle1: {
        fontFamily: tokens.fonts.sans,
        fontSize: '1.08rem',
        fontWeight: 400,
        lineHeight: 1.68,
        color: c.textSecondary,
      },
      subtitle2: {
        fontFamily: tokens.fonts.sans,
        fontSize: '0.9rem',
        fontWeight: 500,
        lineHeight: 1.5,
        color: c.textSecondary,
      },
      body1: {
        fontFamily: tokens.fonts.sans,
        fontSize: '1rem',
        lineHeight: 1.76,
        color: c.textPrimary,
      },
      body2: {
        fontFamily: tokens.fonts.sans,
        fontSize: '0.9rem',
        lineHeight: 1.68,
        color: c.textSecondary,
      },
      caption: {
        fontFamily: tokens.fonts.mono,
        fontSize: '0.78rem',
        letterSpacing: '0.04em',
        color: c.textMuted,
      },
      overline: {
        fontFamily: tokens.fonts.mono,
        fontSize: '0.72rem',
        fontWeight: 600,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: c.accentSecondary,
      },
      button: {
        fontFamily: tokens.fonts.sans,
        fontWeight: 600,
        fontSize: '0.88rem',
        textTransform: 'none',
        letterSpacing: '0.01em',
      },
    },
    shape: {
      borderRadius: 14,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.bgPrimary,
            color: c.textPrimary,
            scrollbarColor: `${c.borderMedium} ${c.bgPrimary}`,
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-thumb': {
              borderRadius: '9999px',
              backgroundColor: c.borderMedium,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: c.bgPrimary,
            },
            '&::selection': {
              backgroundColor: mode === 'dark' ? 'rgba(229, 169, 60, 0.3)' : 'rgba(184, 134, 11, 0.22)',
              color: c.textPrimary,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            padding: '8px 20px',
            transition: 'all 200ms cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: mode === 'dark' ? '0 6px 20px rgba(0, 0, 0, 0.4)' : '0 6px 20px rgba(0, 0, 0, 0.08)',
              transform: 'translateY(-1px)',
            },
            '&:focus-visible': {
              outline: `2px solid ${c.accentSecondary}`,
              outlineOffset: '2px',
            },
          },
          containedPrimary: {
            backgroundColor: c.accentPrimary,
            color: mode === 'dark' ? '#0a0c10' : '#ffffff',
            fontWeight: 700,
            border: `1px solid ${c.accentBorder}`,
            boxShadow: mode === 'dark' ? '0 4px 18px rgba(229, 169, 60, 0.28)' : '0 4px 14px rgba(180, 83, 9, 0.2)',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#f5b84c' : '#92400e',
              boxShadow: mode === 'dark' ? '0 6px 24px rgba(229, 169, 60, 0.45)' : '0 6px 20px rgba(180, 83, 9, 0.3)',
              transform: 'translateY(-2px)',
            },
          },
          containedSecondary: {
            backgroundColor: c.accentSecondary,
            color: mode === 'dark' ? '#0a0c10' : '#ffffff',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#cca030' : '#9c7108',
            },
          },
          outlined: {
            borderColor: c.borderMedium,
            color: c.textPrimary,
            backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.6)',
            backdropFilter: 'blur(8px)',
            '&:hover': {
              borderColor: c.accentSecondary,
              backgroundColor: mode === 'dark' ? 'rgba(229, 169, 60, 0.08)' : 'rgba(184, 134, 11, 0.08)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgCard,
            backgroundImage: 'none',
            borderRadius: '16px',
            border: `1px solid ${c.borderMedium}`,
            boxShadow: tokens.shadows.card,
            position: 'relative',
            backdropFilter: 'blur(12px)',
            transition: 'all 260ms cubic-bezier(0.16, 1, 0.3, 1)',
            '&:hover': {
              borderColor: c.accentSecondary,
              boxShadow: tokens.shadows.cardHover,
              transform: 'translateY(-3px)',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            fontWeight: 500,
            fontSize: '0.74rem',
            fontFamily: tokens.fonts.mono,
            height: '26px',
            transition: 'all 150ms ease',
          },
          outlined: {
            borderColor: c.tagBorder,
            backgroundColor: c.tagBg,
            color: c.tagText,
          },
          filled: {
            backgroundColor: c.accentMuted,
            color: c.accentPrimary,
            border: `1px solid ${c.accentBorder}`,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: c.borderSubtle,
          },
        },
      },
    },
  });
};

const defaultTheme = getTheme('dark');
export default defaultTheme;
